import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Wrench, ShoppingCart, CreditCard, AlertTriangle, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/api/dashboard`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const statusLabels = {
    available: 'Disponible',
    waiting_part: 'Esperando pieza',
    waiting_client: 'Esperando cliente',
    outsourcing: 'Servicio externo',
    liberacion: 'Liberacion',
    archived: 'Archivado',
    done: 'Terminado',
    delivered: 'Entregado'
  };

  const statusColors = {
    available: 'bg-blue-100 text-blue-700',
    waiting_part: 'bg-amber-100 text-amber-700',
    waiting_client: 'bg-purple-100 text-purple-700',
    outsourcing: 'bg-indigo-100 text-indigo-700',
    liberacion: 'bg-cyan-100 text-cyan-700',
    archived: 'bg-slate-100 text-slate-700',
    done: 'bg-green-100 text-green-700',
    delivered: 'bg-emerald-100 text-emerald-700'
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-slate-400">Cargando...</div></div>;
  }

  return (
    <div data-testid="dashboard-page">
      <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit'] mb-6">Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="stat-card" data-testid="stat-repairs">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1D3C6E]/10 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-[#1D3C6E]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{data?.total_repairs || 0}</p>
              <p className="text-sm text-slate-500">Reparaciones</p>
            </div>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-sales">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#AFBD20]/10 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-[#AFBD20]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">${data?.sales_today?.toFixed(2) || '0.00'}</p>
              <p className="text-sm text-slate-500">Ventas hoy ({data?.sales_count_today || 0})</p>
            </div>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-credits">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">${data?.total_credits_balance?.toFixed(2) || '0.00'}</p>
              <p className="text-sm text-slate-500">Creditos pendientes ({data?.pending_credits || 0})</p>
            </div>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-low-stock">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{data?.low_stock_count || 0}</p>
              <p className="text-sm text-slate-500">Stock bajo</p>
            </div>
          </div>
        </div>
      </div>

      {/* Repairs by Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="repairs-status-section">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Outfit']">Reparaciones por Estado</h3>
          <div className="space-y-2">
            {Object.entries(statusLabels).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50">
                <div className="flex items-center gap-2">
                  <span className={`status-badge ${statusColors[key]}`}>{label}</span>
                </div>
                <span className="text-sm font-semibold text-slate-700">
                  {data?.repairs_by_status?.[key] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Repairs */}
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="recent-repairs-section">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Outfit']">Reparaciones Recientes</h3>
          {data?.recent_repairs?.length > 0 ? (
            <div className="space-y-3">
              {data.recent_repairs.map((repair) => (
                <div key={repair.id} className="flex items-center justify-between py-2 px-3 rounded-lg border border-slate-100">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{repair.ticket}</p>
                    <p className="text-xs text-slate-500">{repair.client_name} - {repair.device_model}</p>
                  </div>
                  <span className={`status-badge ${statusColors[repair.status] || 'bg-slate-100 text-slate-700'}`}>
                    {statusLabels[repair.status] || repair.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No hay reparaciones recientes</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
